import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";

const CartSheet = ({ cart, isOpen, onClose, onUpdateItem, onRemoveItem }) => {
  const isEmpty = !cart?.items || cart.items.length === 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            data-testid="cart-backdrop"
          />

          {/* Sheet */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md z-50 glass border-l border-border"
            data-testid="cart-sheet"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h2 className="font-unbounded text-xl font-semibold text-foreground">
                  Your Cart
                </h2>
                <Button
                  data-testid="close-cart-btn"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="hover:bg-muted"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Cart Items */}
              {isEmpty ? (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                    <ShoppingBag className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <p className="font-outfit text-muted-foreground mb-4">
                    Your cart is empty
                  </p>
                  <Button
                    data-testid="start-shopping-btn"
                    onClick={onClose}
                    asChild
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Link to="/shop">Start Shopping</Link>
                  </Button>
                </div>
              ) : (
                <>
                  <ScrollArea className="flex-1 p-6">
                    <div className="space-y-4">
                      {cart.items.map((item) => (
                        <motion.div
                          key={item.product_id}
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="flex gap-4 p-4 rounded-xl bg-card border border-border"
                          data-testid={`cart-item-${item.product_id}`}
                        >
                          {/* Product Image */}
                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Product Details */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-outfit font-medium text-sm text-foreground truncate">
                              {item.product_name}
                            </h3>
                            <p className="text-primary font-semibold mt-1">
                              ${item.price.toFixed(2)}
                            </p>

                            {/* Quantity Controls */}
                            <div className="flex items-center gap-2 mt-2">
                              <Button
                                data-testid={`decrease-qty-${item.product_id}`}
                                variant="outline"
                                size="icon"
                                className="w-7 h-7 border-border hover:bg-muted"
                                onClick={() =>
                                  onUpdateItem(item.product_id, item.quantity - 1)
                                }
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="font-outfit text-sm w-8 text-center">
                                {item.quantity}
                              </span>
                              <Button
                                data-testid={`increase-qty-${item.product_id}`}
                                variant="outline"
                                size="icon"
                                className="w-7 h-7 border-border hover:bg-muted"
                                onClick={() =>
                                  onUpdateItem(item.product_id, item.quantity + 1)
                                }
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Button
                                data-testid={`remove-item-${item.product_id}`}
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7 ml-auto text-accent hover:bg-accent/10"
                                onClick={() => onRemoveItem(item.product_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Footer */}
                  <div className="p-6 border-t border-border">
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="font-outfit text-sm text-muted-foreground">Subtotal</span>
                        <span className="font-outfit text-sm text-foreground">
                          ${cart.subtotal?.toFixed(2) || cart.total?.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-outfit text-sm text-muted-foreground">
                          Tax ({((cart.tax_rate || 0.0948) * 100).toFixed(2)}%)
                        </span>
                        <span className="font-outfit text-sm text-foreground">
                          ${cart.tax?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                      <Separator className="bg-border" />
                      <div className="flex items-center justify-between">
                        <span className="font-outfit text-muted-foreground">Total</span>
                        <span className="font-unbounded text-xl font-bold text-primary">
                          ${cart.total?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <Button
                      data-testid="checkout-btn"
                      onClick={onClose}
                      asChild
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-outfit font-semibold py-6 text-lg rounded-xl neon-box"
                    >
                      <Link to="/checkout">Proceed to Checkout</Link>
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-3 font-outfit">
                      Store pickup only • Gardner, KS
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartSheet;
